import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Courses.css";
import { useTranslation } from "react-i18next";
import { getCourses } from "../../../../api/courses";
import { useNavigate } from "react-router-dom";
import { COURSE_IMAGES } from "./courseImages";

import arrowLeft from "../../../../assets/icons/left.svg";
import arrowRight from "../../../../assets/icons/right.svg";


const coursesData = [
    {
        id: 1,
        title: "BUILDERS & CONTRACTORS",
        desc: "On-site installation best practices",
        image: COURSE_IMAGES[0],
    },
    {
        id: 2,
        title: "ENGINEERS",
        image: COURSE_IMAGES[1],
    },
    {
        id: 3,
        title: "DESIGNERS",
        image: COURSE_IMAGES[2],
    },
    {
        id: 4,
        title: "ALL SKILL LEVELS",
        image: COURSE_IMAGES[3],
    },
];

const Courses = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const sliderRef = useRef(null);
    const [apiCourses, setApiCourses] = useState([]);
    const isRtl = i18n.dir(i18n.language) === "rtl";

    let isDown = false;
    let startX;
    let scrollLeft;

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const data = await getCourses({
                    language: String(i18n.language || "en").toLowerCase(),
                });
                if (!isMounted) return;
                const raw = Array.isArray(data) ? data : [];
                const normalized = raw
                    .map((item) => ({
                        id: item?.id,
                        title: item?.title || "",
                        desc: item?.description || "",
                    }))
                    .filter((item) => item.title);
                const seenId = new Set();
                const seenTitle = new Set();
                const unique = [];
                for (const item of normalized) {
                    const titleKey = item.title.trim().toLowerCase();
                    if (item.id != null && item.id !== "") {
                        if (seenId.has(item.id)) continue;
                        seenId.add(item.id);
                        unique.push(item);
                        continue;
                    }
                    if (seenTitle.has(titleKey)) continue;
                    seenTitle.add(titleKey);
                    unique.push(item);
                }
                setApiCourses(unique);
            } catch {
                if (isMounted) setApiCourses([]);
            }
        };
        load();
        return () => {
            isMounted = false;
        };
    }, [i18n.language]);

    const list = useMemo(() => {
        const raw = apiCourses.length ? apiCourses : coursesData;
        const seenId = new Set();
        const seenTitle = new Set();
        const unique = [];
        for (const course of raw) {
            const titleKey = String(course.title || "").trim().toLowerCase();
            if (course.id != null && course.id !== "") {
                if (seenId.has(course.id)) continue;
                seenId.add(course.id);
                unique.push(course);
                continue;
            }
            if (titleKey) {
                if (seenTitle.has(titleKey)) continue;
                seenTitle.add(titleKey);
                unique.push(course);
            }
        }
        return unique.map((course, index) => ({
            ...course,
            image: COURSE_IMAGES[index % COURSE_IMAGES.length],
        }));
    }, [apiCourses]);

    const handleMouseDown = (e) => {
        isDown = true;
        sliderRef.current.classList.add("active");
        startX = e.pageX - sliderRef.current.offsetLeft;
        scrollLeft = sliderRef.current.scrollLeft;
    };

    const handleMouseLeave = () => {
        isDown = false;
    };

    const handleMouseUp = () => {
        isDown = false;
    };

    const handleMouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    const scroll = (direction) => {
        const container = sliderRef.current;
        if (!container) return;
        const scrollAmount = 320;
        const logicalDirection = isRtl
            ? (direction === "left" ? "right" : "left")
            : direction;
        container.scrollBy({
            left: logicalDirection === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        });
    };

    return (
        <section className="courses">
            <div className="courses-header">
                <span className="courses-tag">{t("sections.academy.courses.tag")}</span>

                <h2 className="courses-title">
                    {t("sections.academy.courses.title")}{" "}
                    <span>{t("sections.academy.courses.titleAccent")}</span>
                </h2>

                <p className="courses-text">{t("sections.academy.courses.text")}</p>

            </div>

            {/* SLIDER */}
            <div
                className="courses-slider"
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                {list.map((item) => (
                    <div
                        className="course-card"
                        key={String(item.id ?? item.title)}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            if (item.id == null || item.id === "") return;
                            navigate(`/academy/courses/${item.id}`, {
                                state: { heroImage: item.image },
                            });
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (item.id == null || item.id === "") return;
                                navigate(`/academy/courses/${item.id}`, {
                                    state: { heroImage: item.image },
                                });
                            }
                        }}
                    >
                        <img src={item.image} alt="" />

                        <div className="overlay grey"></div>
                        <div className="overlay orange"></div>

                        <div className="course-content">
                            <h3>{item.title}</h3>
                            {item.desc && <p>{item.desc}</p>}
                        </div>
                    </div>
                ))}
            </div>

            <div className="courses-arrows">
                <button
                    type="button"
                    onClick={() => scroll("left")}
                    className="courses-arrowBtn"
                    aria-label="Scroll courses left"
                >
                    <img src={isRtl ? arrowRight : arrowLeft} alt="" draggable={false} />
                </button>
                <button
                    type="button"
                    onClick={() => scroll("right")}
                    className="courses-arrowBtn"
                    aria-label="Scroll courses right"
                >
                    <img src={isRtl ? arrowLeft : arrowRight} alt="" draggable={false} />
                </button>
            </div>
        </section>
    );
};

export default Courses;